import { memo } from 'react';
import { StatusIndicator } from '@amzn/awsui-components-core';
import { ContentType } from '@amzn/awscat-pattern-library-service-model';

import { GuideReadonlyView } from './GuideReadonlyView';
import { PatternReadonlyView } from './PatternReadonlyView';
import { useContent } from '../v2-hooks/useContent';

interface ContentsReadonlyTabViewProps {
  /** Whether the Guide or Pattern body reports the view page's load time once it renders. */
  readonly isPageLoadTracked: boolean;
}

/** The "Contents" tab body on the read-only view page. Dispatches to the renderer for the loaded content type. */
export const ContentsReadonlyTabView = memo(function ContentsReadonlyTabView({
  isPageLoadTracked,
}: ContentsReadonlyTabViewProps) {
  const content = useContent();

  // The source can transiently clear content on reload or document switch; show
  // a loader rather than treating a not-yet-loaded document as unsupported.
  if (!content) {
    return <StatusIndicator type="loading">Loading content</StatusIndicator>;
  }

  switch (content.contentMetadata.contentType) {
    case ContentType.Guide:
    case ContentType.Strategy:
      // Strategy shares the Guide format, so it uses the Guide renderer.
      return <GuideReadonlyView isPageLoadTracked={isPageLoadTracked} />;
    case ContentType.Pattern:
      return <PatternReadonlyView isPageLoadTracked={isPageLoadTracked} />;
    default:
      // Throw so the nearest error boundary reports unsupported content types
      // loudly instead of silently rendering an empty body.
      throw new Error(`Content type "${content.contentMetadata.contentType}" is not supported.`);
  }
});
